import dayjs from "dayjs";
import { toCalendarDate } from "./calendarDate";

// Every date here is read as the calendar day it was picked on, not as an
// instant in the browser's zone, so the payroll Lambda and this page agree on
// which month a date falls in.
const calendarDay = (value) => {
  const date = toCalendarDate(value);
  return date ? dayjs(date) : null;
};

// Subjective tax liability period (PMK 168/2023 Appendix B point I.2.1.2).
//
// Net income is annualized ONLY when the subjective tax liability itself
// begins or ends mid-year: a foreign national arriving in Indonesia, someone
// leaving Indonesia permanently, or death. Being hired or changing jobs
// mid-year is example I.2.1.1, which is not annualized, and that is the
// majority of cases. So without both of these dates the answer is always
// `false`.
// The day the employee's tax year ends: the last day of employment or the day
// the subjective tax liability ends (leaving Indonesia for good, or death),
// whichever comes first. That month is the last tax period under PMK 168/2023.
// Null when neither date is set - the year runs to December.
export const getTaxYearEndDate = (employee) => {
  const dates = [employee?.endDate, employee?.taxSubjectEndDate]
    .map(calendarDay)
    .filter((date) => date && date.isValid());

  if (dates.length === 0) return null;
  return dates.reduce((earliest, date) =>
    date.isBefore(earliest) ? date : earliest
  );
};

export const getAnnualisation = (employee, currentYear) => {
  const yearStart = dayjs(`${currentYear}-01-01`);
  const yearEnd = dayjs(`${currentYear}-12-31`);

  const startDate = calendarDay(employee?.startDate) || yearStart;
  const firstMonth = startDate.year() === currentYear ? startDate : yearStart;
  // Income stops when the tax year ends for the employee, so a liability that
  // ends in June counts six months even with no end of employment on file.
  const rawEndDate = getTaxYearEndDate(employee) || yearEnd;
  const lastMonth = rawEndDate.isAfter(yearEnd) ? yearEnd : rawEndDate;

  const subjectStart = calendarDay(employee?.taxSubjectStartDate);
  const subjectEnd = calendarDay(employee?.taxSubjectEndDate);

  const annualise = Boolean(
    (subjectStart?.year() === currentYear &&
      subjectStart.isAfter(yearStart, "day")) ||
      (subjectEnd?.year() === currentYear &&
        subjectEnd.isBefore(yearEnd, "day"))
  );

  // The annualization denominator: months with income in this tax year. It has
  // to be known before the last tax period is calculated, so it cannot be
  // taken from the number of rows already built.
  const monthsWorked = Math.min(
    Math.max(lastMonth.diff(firstMonth.startOf("month"), "month") + 1, 1),
    12
  );

  return { annualise, monthsWorked };
};
